import { useChainModal, useConnectModal } from '@rainbow-me/rainbowkit'
import { isAddress } from 'ethers/lib/utils.js'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Address, useNetwork } from 'wagmi'

import MoonIcon from 'assets/svg/app/moon.svg'
import SunIcon from 'assets/svg/app/sun.svg'
import Button from 'components/Button'
import Connector from 'containers/Connector'
import { useAutoConnect } from 'hooks/useAutoConnect'
import useClickOutside from 'hooks/useClickOutside'
import useENS from 'hooks/useENS'
import { StakingCard } from 'sections/dashboard/Stake/card'
import WatchOrConnectWallet, {
	WatcherModeIcon,
} from 'sections/futures/WatcherMode/WatchOrConnectWallet'
import { selectSmartMarginAccount } from 'state/futures/smartMargin/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { setTheme } from 'state/preferences/reducer'
import { selectCurrentTheme } from 'state/preferences/selectors'
import { resetWalletAddress } from 'state/wallet/actions'
import { selectIsWatcherMode } from 'state/wallet/selectors'
import media from 'styles/media'

import BalanceActions from './BalanceActions'
import ConnectionDot from './ConnectionDot'
import NetworksSwitcher from './NetworksSwitcher'
import OneClickTradingActions from './OneClickTradingActions'
import WalletActions from './WalletActions'

const WalletButtons: React.FC = () => {
	const { t } = useTranslation()
	const { isWalletConnected } = Connector.useContainer()
	const { chain: network } = useNetwork()
	const dispatch = useAppDispatch()
	const smartMarginAccount = useAppSelector(selectSmartMarginAccount)
	const isWatcherMode = useAppSelector(selectIsWatcherMode)
	const router = useRouter()

	const { openConnectModal } = useConnectModal()
	const [openConnectionDropdown, setOpenConnectionDropdown] = useState(false)
	const { ref } = useClickOutside(() => setOpenConnectionDropdown(false))

	const currentTheme = useAppSelector(selectCurrentTheme)
	const { openChainModal } = useChainModal()

	const walletParam = useMemo(() => {
		const wallet = router.query.wallet as string
		return wallet ?? ''
	}, [router.query.wallet])

	const { ensAddress } = useENS(walletParam)

	useEffect(() => {
		if (!!ensAddress || (!!walletParam && isAddress(walletParam))) {
			dispatch(
				resetWalletAddress({
					address: (ensAddress ?? walletParam) as Address,
					selectedType: 'watcher',
				})
			)
		}
	}, [ensAddress, walletParam, dispatch])

	const ThemeIcon = currentTheme === 'dark' ? SunIcon : MoonIcon

	const toggleTheme = () => {
		dispatch(setTheme(currentTheme === 'light' ? 'dark' : 'light'))
	}

	const handleOpenConnectModal = useCallback(() => {
		setOpenConnectionDropdown(false)
		openConnectModal?.()
	}, [openConnectModal])

	useAutoConnect()
	const WalletIsNotConnected = useCallback(
		() => (
			<>
				<Button
					size="small"
					variant="flat"
					noOutline
					onClick={handleOpenConnectModal}
					data-testid="connect-wallet"
					mono
				>
					<ConnectionDot />
					{t('common.wallet.connect')}
				</Button>
				<Button
					size="small"
					variant="flat"
					noOutline
					onClick={() => setOpenConnectionDropdown((x) => !x)}
					mono
				>
					<WatcherModeIcon />
					{t('common.wallet.watch')}
				</Button>
			</>
		),
		[handleOpenConnectModal, t]
	)

	const WalletIsConnectedButNotSupported = useCallback(
		() => (
			<>
				<Button size="small" mono variant="flat" onClick={openChainModal}>
					{t('homepage.l2.cta-buttons.switch-networks')}
				</Button>
				<Button size="small" variant="flat" data-testid="unsupported-network" mono>
					<ConnectionDot />
					{t('common.wallet.unsupported-network')}
				</Button>
			</>
		),
		[t, openChainModal]
	)

	const WalletIsConnectedAndSupported = useCallback(
		() => (
			<>
				<BalanceActions />
				<WalletActions />
				{!isWatcherMode && (
					<>
						<NetworksSwitcher />
						{!!smartMarginAccount && <OneClickTradingActions />}
					</>
				)}
			</>
		),
		[smartMarginAccount, isWatcherMode]
	)

	return (
		<Container ref={ref}>
			{isWalletConnected ? (
				network?.unsupported ? (
					<WalletIsConnectedButNotSupported />
				) : (
					<WalletIsConnectedAndSupported />
				)
			) : (
				<WalletIsNotConnected />
			)}
			{openConnectionDropdown && (
				<WatchOrConnectWalletContainer>
					<WatchOrConnectWallet onDismiss={() => setOpenConnectionDropdown(false)} />
				</WatchOrConnectWalletContainer>
			)}
			<MenuButton onClick={toggleTheme} noOutline>
				<ThemeIcon width={20} />
			</MenuButton>
		</Container>
	)
}

const Container = styled.div`
	display: grid;
	grid-gap: 15px;
	grid-auto-flow: column;
`

const MenuButton = styled(Button)`
	display: grid;
	place-items: center;
	height: 41px;
	width: 41px;
	padding: 0px;

	svg {
		path {
			fill: ${(props) => props.theme.colors.selectedTheme.icon.fill};
		}
	}

	:hover {
		svg {
			path {
				fill: ${(props) => props.theme.colors.selectedTheme.icon.hover};
			}
		}
	}
`
const WatchOrConnectWalletContainer = styled(StakingCard)`
	z-index: 100;
	position: fixed;
	top: 15px;
	right: 70px;
	display: flex;
	flex-direction: column;
	width: 300px;
	row-gap: 20px;

	${media.lessThan('mdUp')`
		padding: 15px;
	`}

	${media.greaterThan('mdUp')`
		margin-top: 56px;
	`}
`

export default WalletButtons
